import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useNavigationManager } from '../../../core';
import { logger } from '../../../core';
import { RevoltIcon } from '../../../components/RevoltIcon';

function LoginForm({ onSwitchToRegister, onSwitchToForgot }) {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const navigationManager = useNavigationManager();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect authenticated users - backend enforces beta testing per route
  useEffect(() => {
    if (isAuthenticated) {
      setLoading(false);
      // Try to complete auth redirect first (return to previous page)
      logger.log(
        { tags: 'section|auth|login', color1: 'cyan' },
        'Authenticated and beta tester, attempting auth redirect'
      );
      const redirectCompleted = navigationManager.completeAuthRedirect();
      if (!redirectCompleted) {
        // No return data, default to main section
        logger.log(
          { tags: 'section|auth|login', color1: 'yellow' },
          'No auth return data, navigating to /main'
        );
        navigate('/main');
      }
    }
  }, [isAuthenticated, navigate, navigationManager]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    logger.log(
      { tags: 'section|auth|login', color1: 'cyan' },
      'Submitting login',
      { email: formData.email }
    );

    const result = await login(formData.email, formData.password);

    if (!result.success) {
      setError(result.error || 'Login failed');
      setLoading(false);
      logger.error(
        { tags: 'section|auth|login', color1: 'red' },
        'Login failed',
        { error: result.error }
      );
    }
    // Login successful - AuthContext will update user state,
    // and useEffect will handle the redirect
  };


  return (
    <form className="auth-form" onSubmit={handleSubmit} autoComplete="on">
      <div className="auth-header">
        <h1 className="auth-title">Welcome Back</h1>
        <p className="auth-subtitle">Sign in to your account</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-group">
        <label className="form-label" htmlFor="email">
          Email
        </label>
        <input
          type="email"
          id="email"
          name="email"
          className="form-input"
          placeholder="you@example.com"
          autoComplete="username"
          value={formData.email}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="password">
          Password
        </label>
        <div className="password-wrap">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            className="form-input password-input"
            placeholder="••••••••"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            required
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            onMouseDown={(e) => {
              // Keep focus on the input when toggling.
              e.preventDefault();
            }}
          >
            <RevoltIcon
              name={showPassword ? 'eye-off' : 'eye'}
              source="lucide"
              size="sm"
            />
          </button>
        </div>
      </div>

      <div className="form-group" style={{ textAlign: 'right', marginBottom: '10px' }}>
        <button
          type="button"
          className="form-link"
          onClick={onSwitchToForgot}
          style={{ fontSize: '0.85rem' }}
        >
          Forgot Password?
        </button>
      </div>

      <button type="submit" className="form-button" disabled={loading}>
        {loading ? 'Signing In...' : 'Sign In'}
      </button>

      <div className="form-footer">
        Don't have an account?{' '}
        <button
          type="button"
          className="form-link"
          onClick={onSwitchToRegister}
        >
          Sign up
        </button>
      </div>

      <style>{`
        .auth-form {
          width: 100%;
        }

        .auth-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .auth-title {
          font-size: 1.8rem;
          font-weight: 300;
          margin-bottom: 8px;
          background: linear-gradient(135deg, #ffffff 0%, #ff4444 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .auth-subtitle {
          font-size: 0.95rem;
          color: #999;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          font-size: 0.9rem;
          color: #ccc;
          font-weight: 500;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px;
          background: rgba(30, 30, 30, 0.6);
          border: 1px solid rgba(139, 0, 0, 0.3);
          border-radius: 8px;
          color: #fff;
          font-size: 0.95rem;
        }

        .form-input:focus {
          outline: none;
          border-color: #8b0000;
        }

        .form-input:-webkit-autofill,
        .form-input:-webkit-autofill:hover,
        .form-input:-webkit-autofill:focus,
        .form-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px rgba(30, 30, 30, 0.6) inset !important;
          -webkit-text-fill-color: #fff !important;
          background: rgba(30, 30, 30, 0.6) !important;
          border: 1px solid rgba(139, 0, 0, 0.3) !important;
          border-radius: 8px !important;
          transition: background-color 5000s ease-in-out 0s !important;
        }

        .form-input:valid,
        .form-input:not(:placeholder-shown) {
          background: rgba(30, 30, 30, 0.6) !important;
          color: #fff !important;
          border-color: rgba(139, 0, 0, 0.3) !important;
        }

        .password-wrap {
          position: relative;
        }

        .password-input {
          padding-right: 44px;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          border: none;
          background: none;
          padding: 0;
          margin: 0;
          cursor: pointer;
          color: #ccc;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .password-toggle:hover {
          color: #ff4444;
        }

        .form-button {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #8b0000, #ff4444);
          border: none;
          border-radius: 8px;
          color: white;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          margin-bottom: 20px;
        }

        .form-button:hover:not(:disabled) {
          transform: translateY(-2px);
        }

        .form-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .form-footer {
          text-align: center;
          font-size: 0.9rem;
          color: #999;
        }

        .form-link {
          color: #ff4444;
          background: none;
          border: none;
          cursor: pointer;
          text-decoration: underline;
          font-size: inherit;
        }

        .alert {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 0.9rem;
        }

        .alert-error {
          background: rgba(220, 53, 69, 0.1);
          border: 1px solid rgba(220, 53, 69, 0.3);
          color: #f87171;
        }
      `}</style>
    </form>
  );
}

export default LoginForm;
